import React, { useState, useEffect, useRef } from 'react';
import { useTouchLikeInteraction } from '../../hooks/useTouchLikeInteraction';
import './ChatSidebar.css';

const EMOJI_POOL = ['🧠', '🌐', '📡', '🧿', '👁️', '🌀', '🕸️', '🔮', '💎', '🛸', '🛰️', '🧬', '🧪', '👾', '🤖', '⚡', '🔥', '🌑', '🌕', '🪐'];

const getDeterministicEmoji = (seed) => {
  if (!seed) return EMOJI_POOL[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash |= 0;
  }
  return EMOJI_POOL[Math.abs(hash) % EMOJI_POOL.length];
};

const ChatSidebar = ({ chats, activeChat, onSelectChat, onNewChat, onNewGroup, onRenameChat, onDeleteChat, t }) => {
  const isTouch = useTouchLikeInteraction();
  const [qophExpanded, setQophExpanded] = useState(false);
  const [groupsExpanded, setGroupsExpanded] = useState(false);
  const [openGroupMenu, setOpenGroupMenu] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenGroupMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const qophChats = chats.filter(c => c.type === 'qoph');
  const groupChats = chats.filter(c => c.type === 'group');

  const renderAvatar = (chat) => {
    const emoji = getDeterministicEmoji(chat.title + (chat.manifesto || ''));
    const isGroup = chat.type === 'group';

    const handleIconClick = (e) => {
      if (isGroup) {
        e.stopPropagation();
        setOpenGroupMenu(openGroupMenu === chat.id ? null : chat.id);
      }
    };

    return (
      <div
        className={`chat-avatar ${chat.type}-avatar ${isGroup ? 'group-clickable' : ''}`}
        onClick={handleIconClick}
      >
        {emoji}
        {isGroup && openGroupMenu === chat.id && chat.members && (
          <div className="group-members-dropdown" ref={menuRef}>
            <div className="dropdown-header">{t('network.members') || 'Members'}</div>
            {chat.members.map(member => (
              <div key={member.id} className="member-row">
                <span className="member-avatar">{member.avatar || '👤'}</span>
                <span className="member-name">{member.displayAlias || member.username}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleRename = (e, chat) => {
    e.stopPropagation();
    e.preventDefault();
    const newTitle = prompt(t('tasks.renamePrompt') || 'New title:', chat.title);
    if (newTitle && newTitle.trim() && newTitle !== chat.title) {
      onRenameChat?.(chat.id, newTitle.trim());
    }
  };

  const handleDelete = (e, chat) => {
    e.stopPropagation();
    e.preventDefault();
    if (window.confirm(t('qoph.deleteConfirm') || 'Delete this connection?')) {
      onDeleteChat?.(chat.id);
    }
  };

  const renderChatList = (chatList) => (
    chatList.map(chat => {
      const tooltipText = chat.lastMessage || chat.firstQuery || chat.manifesto || '';
      const isQoph = chat.type === 'qoph';

      return (
        <div
          key={`${chat.type}-${chat.id}`}
          className={`chat-item ${activeChat?.id === chat.id && activeChat?.type === chat.type ? 'active' : ''}`}
          title={tooltipText}
        >
          {renderAvatar(chat)}
          <div className="chat-info" onClick={() => onSelectChat(chat)}>
            <div className="chat-header-row">
              <span className="chat-title">{chat.title}</span>
              <span className="chat-date">
                {chat.updatedAt ? new Date(chat.updatedAt).toLocaleDateString([], { day: '2-digit', month: '2-digit' }) : ''}
              </span>
            </div>
            <div className="chat-preview">
              <span className="preview-text">{chat.manifesto || ''}</span>
            </div>
          </div>

          {isQoph && (
            <div className={`chat-item-actions ${isTouch ? 'is-touch' : ''}`}>
              <button
                className="action-btn"
                onClick={(e) => handleRename(e, chat)}
                title={t('common.edit')}
              >
                ✏️
              </button>
              <button
                className="action-btn delete"
                onClick={(e) => handleDelete(e, chat)}
                title={t('common.delete')}
              >
                🗑️
              </button>
            </div>
          )}
        </div>
      );
    })
  );

  return (
    <div className="chat-sidebar">
      <div className="sidebar-header">
        <h3>{t('qoph.historyTitle') || 'CHATS'}</h3>
      </div>

      <div className="chat-sections">
        {/* SECTION 1: QOPH */}
        <div className={`sidebar-section ${qophExpanded ? 'expanded' : ''}`}>
          <div className="section-header" onClick={() => setQophExpanded(!qophExpanded)}>
            <span className="section-icon">🧠</span>
            <span className="section-title">QOPH</span>
            <span className="section-arrow">{qophExpanded ? '▼' : '▶'}</span>
          </div>
          {qophExpanded && (
            <div className="section-content">
              <div className={`chat-item create-item ${activeChat?.id === 'draft' ? 'active' : ''}`} onClick={onNewChat}>
                <div className="chat-avatar qoph-avatar">➕</div>
                <div className="chat-info">
                  <span className="chat-title">{t('qoph.newChat') || 'Nueva conexión'}</span>
                </div>
              </div>
              {qophChats.length === 0 ? (
                <p className="empty-section">{t('qoph.noHistory') || 'No connections'}</p>
              ) : renderChatList(qophChats)}
            </div>
          )}
        </div>

        {/* SECTION 2: GRUPOS MENTALES */}
        <div className={`sidebar-section ${groupsExpanded ? 'expanded' : ''}`}>
          <div className="section-header" onClick={() => setGroupsExpanded(!groupsExpanded)}>
            <span className="section-icon">🕸️</span>
            <span className="section-title">{t('network.groups').toUpperCase() || 'GRUPOS MENTALES'}</span>
            <span className="section-arrow">{groupsExpanded ? '▼' : '▶'}</span>
          </div>
          {groupsExpanded && (
            <div className="section-content">
              <div className="chat-item create-item group-create" onClick={onNewGroup}>
                <div className="chat-avatar group-avatar">➕</div>
                <div className="chat-info">
                  <span className="chat-title">{t('network.createGroup') || 'Nuevo Grupo Mental'}</span>
                </div>
              </div>
              {groupChats.length === 0 ? (
                <p className="empty-section">{t('network.noGroups') || 'No groups found'}</p>
              ) : renderChatList(groupChats)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatSidebar;
